// src/pages/AboutPage.jsx
import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Link,
  Grid,
  Card,
  CardMedia,
  CardContent,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { ChevronRight } from '@mui/icons-material';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

// ---------------- Hero Section ----------------
function AboutHero() {
  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.down('sm'));
  return (
    <Box
      sx={{
        position: 'relative',
        pt: { xs: 6, md: 12 },
        pb: { xs: 4, md: 8 },
        background: theme.palette.grey[100]
      }}
    >
      <Container maxWidth="lg">
        <Typography
          variant="body2"
          color="text.secondary"
          gutterBottom
          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
        >
          <Link href="/" underline="none" color="text.secondary">
            Home
          </Link>
          <ChevronRight sx={{ fontSize: '1rem', color: theme.palette.text.secondary }} />
          <Typography component="span" color="text.secondary">
            About
          </Typography>
        </Typography>
        <Typography variant={isSm ? 'h4' : 'h2'} fontWeight={700} gutterBottom>
          About MRM Lettings
        </Typography>
        {/* <Typography variant="subtitle1" color="text.secondary" mb={4}>
          One dedicated office, local experts, local knowledge
        </Typography> */}
        <Box display="flex" gap={2} flexWrap="wrap">
          <Button variant="contained" href="#team">
            Meet our Team
          </Button>
          {/* <Button variant="outlined" href="#offices">
            Our Offices
          </Button> */}
        </Box>
      </Container>
    </Box>
  );
}

// ---------------- Why Choose Us ----------------
function WhyChoose() {
  return (
    <Box component="section" sx={{ py: { xs: 6, md: 10 }, background: '#fff' }}>
      <Container maxWidth="lg">
        <Typography variant="h3" fontWeight={700} gutterBottom>
          Why choose MRM Lettings?
        </Typography>
        <Typography paragraph>
          MRM Lettings is a family-run firm of property experts serving Teesside since 1994 from our
          single Middlesbrough office. We specialise in sales, lettings and auctions across the region.
        </Typography>
        {/* <Typography paragraph>
          With one high-street office staffed by local experts, we combine in-depth market
          knowledge with the latest marketing technology—giving your home or listing maximum exposure.
        </Typography> */}
        <Typography paragraph>
          As proud members of <Link href="https://www.propertymark.co.uk" target="_blank" rel="noopener">ARLA Propertymark</Link> and 
          <Link href="https://www.naea.co.uk" target="_blank" rel="noopener"> NAEA Propertymark</Link>, we adhere
          to the highest industry standards and client-money protection schemes.
        </Typography>
        <Typography paragraph>
          Whether you’re buying, selling, renting or letting, our end-to-end service covers
          everything from valuation and marketing to legal conveyancing and tenancy management.
        </Typography>
      </Container>
    </Box>
  );
}

// ---------------- Our Story ----------------
function OurStory() {
  return (
    <Box component="section" sx={{ py: { xs: 6, md: 10 }, background: theme => theme.palette.grey[50] }}>
      <Container maxWidth="lg">
        <Typography variant="h4" fontWeight={700} gutterBottom>
          Our story
        </Typography>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Typography paragraph>
              Founded by Michael and Sarah Lettings in 1994, MRM Lettings has always operated from
              a single Middlesbrough base. Our ethos remains the same: provide honest advice, local
              expertise, and a friendly face every step of the way.
            </Typography>
            <Typography paragraph>
              Today Michael’s two sons, David and Richard, help steer the company while our lean
              local team delivers award-winning service to buyers, sellers, landlords and tenants
              throughout the region.
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box
              component="img"
              src="https://via.placeholder.com/600x400?text=Our+Offices"
              alt="MRM Lettings office"
              sx={{ width: '100%', borderRadius: 2 }}
            />
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

// ---------------- Meet the Team ----------------
function MeetTheTeam() {
  const people = [
    { name: 'Michael Lettings', role: 'Founder', photo: 'https://via.placeholder.com/150' },
    { name: 'David Lettings', role: 'Director', photo: 'https://via.placeholder.com/150' },
    { name: 'Richard Lettings', role: 'Director', photo: 'https://via.placeholder.com/150' },
    // ...more team members
  ];

  return (
    <Box component="section" sx={{ py: { xs: 6, md: 10 }, background: '#fff' }} id="team">
      <Container maxWidth="lg">
        <Typography variant="h4" fontWeight={700} gutterBottom>
          Meet our Team
        </Typography>
        <Grid container spacing={4}>
          {people.map((p, i) => (
            <Grid item key={i} xs={12} sm={6} md={4}>
              <Card elevation={2}>
                <CardMedia component="img" height="200" image={p.photo} alt={p.name} />
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {p.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {p.role}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
}

// ---------------- Our Offices ----------------
function OurOffices() {
  const offices = [
    { branch: 'Middlesbrough (Head Office)', address: '142 Victoria Road, Middlesbrough, TS1 3HU' },
  ];

  return (
    <Box component="section" sx={{ py: { xs: 6, md: 10 }, background: theme => theme.palette.grey[50] }} id="offices">
      <Container maxWidth="lg">
        {/* <Typography variant="h4" fontWeight={700} gutterBottom>
          Our Offices
        </Typography>
        <Grid container spacing={4}>
          {offices.map((o, i) => (
            <Grid item key={i} xs={12} sm={6} md={4}>
              <Box sx={{ p: 3, bgcolor: '#fff', borderRadius: 2, boxShadow: 1 }}>
                <Typography variant="h6" gutterBottom>{o.branch}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {o.address}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid> */}
      </Container>
    </Box>
  );
}

// ---------------- The About Us Page ----------------
export default function AboutPage() {
  return (
    <Box>
      <AboutHero />
      <WhyChoose />
      <OurStory />
      <MeetTheTeam />
      <OurOffices />
    </Box>
  );
}
